import React, { useEffect, useState } from 'react';
import {
  DataTable,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableContainer,
  TableSelectAll,
  TableToolbar,
  TableBatchAction,
  TableBatchActions,
  Pagination
} from 'carbon-components-react';
import { Header } from 'redux/member/types';
import DisplayEmailRow from './DisplayEmailRow';
import { EmailResponse } from '../types';
import { Attachment20 } from '@carbon/icons-react';
import { toLocalDateTimeFormatWithSeconds } from 'utilities/helper.util';
import { apiToUi } from './forms/statusData';
import EmptyStatePage from './EmptyState';

const PageSizes = [10, 20, 50];

interface Props {
  emails: Array<EmailResponse> | null;
  pageInfo: any;
  direction: string;
  setApiError: any;
  ignoreEmail: any;
  resendEmail: any;
  setTotalSelected: any;
  totalSelected: any;
  handleSendToOtherClick: Function;
}
const DisplayEmails: React.FC<Props> = ({
  emails,
  pageInfo,
  direction,
  setApiError,
  ignoreEmail,
  resendEmail,
  setTotalSelected,
  totalSelected,
  handleSendToOtherClick
}) => {
  const [filteredEmails, setFilteredEmails] = useState<Array<any>>([]);

  useEffect(() => {
    const selectedEmails =
      emails?.map((email) => {
        let modifiedEmails: any = {
          ...email,
          sent: email?.sent
            ? toLocalDateTimeFormatWithSeconds(email?.sent)
            : '',
          id: email.id?.toString()
        };

        const statusValue:
          | 'bounce'
          | 'deferred'
          | 'delivered'
          | 'dropped'
          | 'open'
          | 'processed'
          | 'spamreport'
          | 'unsubscribed'
          | 'click' = email.status;
        const filteredStatus = apiToUi[statusValue];
        modifiedEmails = { ...modifiedEmails, status: filteredStatus };

        return modifiedEmails;
      }) || [];

    setFilteredEmails(selectedEmails);
  }, [emails, direction]);

  const headers: Array<Header> = [
    {
      key: 'sent',
      header: 'Sent'
    },
    { key: 'to', header: 'Recipient' },
    { key: 'from', header: 'Sender' },
    { key: 'subject', header: 'Subject' },
    { key: 'attachment', header: 'A' },
    { key: 'status', header: 'Status' }
  ];

  const getEmailId = (row: any) => {
    const emailId = row.map((r: any) => {
      return parseInt(r.id);
    });
    return emailId;
  };

  const handleResendClick = (row: any) => {
    const emailId = getEmailId(row);
    resendEmail(emailId);
  };

  const handleIgnoreClick = (row: any, batchAct: any) => {
    const emailId = getEmailId(row);
    ignoreEmail(emailId, batchAct);
  };

  const handleOnCancel = () => {};

  return (
    <>
      <DataTable rows={filteredEmails || []} headers={headers}>
        {({
          rows,
          getTableProps,
          getHeaderProps,
          getRowProps,
          getSelectionProps,
          getToolbarProps,
          getBatchActionProps,
          selectedRows
        }: any) => {
          return (
            <TableContainer>
              <TableToolbar {...getToolbarProps()}>
                <TableBatchActions
                  shouldShowBatchActions={totalSelected > 0 ? true : false}
                  onCancel={handleOnCancel}
                  totalSelected={totalSelected}
                >
                  {setTotalSelected(getBatchActionProps().totalSelected)}
                  <TableBatchAction
                    tabIndex={
                      getBatchActionProps().shouldShowBatchActions ? 0 : -1
                    }
                    onClick={() => handleResendClick(selectedRows)}
                  >
                    Resend
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={
                      getBatchActionProps().shouldShowBatchActions ? 0 : -1
                    }
                    onClick={() => handleSendToOtherClick(selectedRows)}
                  >
                    Send to other
                  </TableBatchAction>
                  <TableBatchAction
                    tabIndex={
                      getBatchActionProps().shouldShowBatchActions ? 0 : -1
                    }
                    onClick={() =>
                      handleIgnoreClick(selectedRows, getBatchActionProps())
                    }
                  >
                    Ignore
                  </TableBatchAction>
                </TableBatchActions>
              </TableToolbar>
              <Table {...getTableProps()}>
                <TableHead>
                  <TableRow>
                    <TableSelectAll {...getSelectionProps()} />
                    <TableHeader
                      key="sent"
                      {...getHeaderProps({ key: 'sent', header: 'Sent' })}
                    >
                      Sent
                    </TableHeader>
                    <TableHeader
                      key="subject"
                      {...getHeaderProps({ key: 'subject', header: 'Subject' })}
                    >
                      Subject
                    </TableHeader>
                    <TableHeader
                      key="attachment"
                      {...getHeaderProps({ key: 'attachment', header: 'A' })}
                    >
                      <Attachment20 />
                    </TableHeader>
                    <TableHeader
                      key="status"
                      {...getHeaderProps({
                        key: 'status',
                        header: 'Status'
                      })}
                    >
                      Status
                    </TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row: any, index: any) => (
                    <DisplayEmailRow
                      row={row}
                      getRowProps={getRowProps}
                      getSelectionProps={getSelectionProps}
                      key={index}
                      direction={direction}
                      setApiError={setApiError}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        }}
      </DataTable>

      {filteredEmails?.length < 1 && <EmptyStatePage />}
      {filteredEmails?.length >= 1 && (
        <Pagination
          onChange={pageInfo.handleFetchNextPage}
          backwardText="Previous page"
          forwardText="Next page"
          itemsPerPageText="Items per page:"
          page={pageInfo.currentPage}
          pageNumberText="Page Number"
          pageSize={pageInfo.pageSize}
          pageSizes={PageSizes}
          totalItems={pageInfo.totalItems}
        />
      )}
    </>
  );
};
DisplayEmails.displayName = 'DisplayEmails';
export default DisplayEmails;
